'use client'

import { userPrincipalAtom } from "@/atoms-store/user-atoms";
import clsx from "clsx";
import { useAtom } from "jotai";
import { usePathname, useRouter } from "next/navigation";
import { ReactNode, useEffect, useState } from "react";
import { RiShieldUserFill } from "react-icons/ri";

import { Button } from "@nextui-org/button";
import { Input } from "@nextui-org/input";
import { Kbd } from "@nextui-org/kbd";
import { Link } from "@nextui-org/link";
import {
	NavbarBrand,
	NavbarContent,
	NavbarItem,
	NavbarMenu,
	NavbarMenuItem,
	NavbarMenuToggle,
	Navbar as NextUINavbar,
} from "@nextui-org/navbar";


import { siteConfig } from "@/config/site";
import NextLink from "next/link";

import {
	IncreffLogo,
	SearchIcon,
} from "@/components/icons";

import ImageWrapper from "@/common/components/image-wrapper/image-wrapper";
import { useAuth } from "@/common/context/auth-context";
import useCookie from "@/common/hooks/useCookie";
import { ExtendedRoute } from "@/types/route";
import { Avatar, Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from "@nextui-org/react";
import { FaCircleUser } from "react-icons/fa6";
import { MdOutlineChevronRight, MdPowerSettingsNew, MdSupervisedUserCircle } from "react-icons/md";
import { ADMIN_ROUTES, DOMAIN_ROUTES, PROFILE_ROUTE } from "./navigation/route.config";

export const Navbar = () => {
	const [currentMenu, setCurrentMenu] = useState<Partial<ExtendedRoute> | undefined>();
	const { isAuthenticated, accessLevel, loading } = useAuth();
	// const [userInfo, setUserInfo] = useState({ name: "", org_name: "" });
	const [userPrinciple] = useAtom(userPrincipalAtom);
	const router = useRouter();
	const pathname = usePathname();
	const { getCookie } = useCookie();

	useEffect(() => {
		console.log('router from navbar', pathname);
		const menuItem = [...ADMIN_ROUTES, ...DOMAIN_ROUTES, ...PROFILE_ROUTE].find((item) => item.path === pathname);
		setCurrentMenu(menuItem);
	}, [pathname]);

	useEffect(() => {
		const updateNavbarHeight = () => {
			const navbarElement = document.querySelector('nav');
			const titlebarElement = document.getElementById('titlebar');

			if (!navbarElement) return;

			let navbarHeight = '0px';
			let titlebarHeight = '0px';

			if (pathname === '/logout' || !currentMenu?.title) navbarHeight = '0px';
			else navbarHeight = '3.5rem';

			if(!currentMenu?.showTitleBar) titlebarHeight = '0px';
			else titlebarHeight = '3.125rem';

			document.body.style.setProperty('--navbar-height', navbarHeight);
			document.body.style.setProperty('--titlebar-height', titlebarHeight);

			// Force a reflow to ensure the main container updates
			document.body.style.minHeight = document.body.style.minHeight;
		};

		// Initial update
		updateNavbarHeight();

		// Add resize listener to update height if the navbar changes size
		window.addEventListener('resize', updateNavbarHeight);

		// Add a short delay to ensure the navbar has fully rendered
		const timeoutId = setTimeout(updateNavbarHeight, 0);

		return () => {
			window.removeEventListener('resize', updateNavbarHeight);
			clearTimeout(timeoutId);
		};
	}, [pathname, currentMenu]);

	useEffect(() => console.log('userPrincipalAtom from navbar', userPrinciple), [userPrinciple]);

	// useEffect(() => {
	//     const token = getCookie(COOKIE_KEY);
	//     if (token) {
	//         try {
	//             const decodedToken = jwtDecode(token) as UserPayload;
	//             setUserInfo({
	//                 name: decodedToken.name || decodedToken.username || "Unknown",
	//                 org_name: decodedToken.org_name || "Unknown domain",
	//             });
	//         } catch (error) {
	//             console.error('Error decoding token:', error);
	//         }
	//     }
	// }, []);

	const searchInput = (
		<Input
			aria-label="Search"
			classNames={{
				inputWrapper: "bg-default-100",
				input: "text-sm",
			}}
			endContent={
				<Kbd className="hidden lg:inline-block" keys={["command"]}>
					K
				</Kbd>
			}
			labelPlacement="outside"
			placeholder="Search..."
			startContent={
				<SearchIcon className="flex-shrink-0 text-base pointer-events-none text-default-400" />
			}
			type="search"
		/>
	);

	const AdminDropdown = (
		<Dropdown placement="bottom-start">
			<DropdownTrigger>
				<Button
					disableRipple
					className="p-0 bg-transparent data-[hover=true]:bg-transparent gap-1"
					radius="sm"
					variant="light"
					startContent={<RiShieldUserFill className="text-base text-increff-orange-500 " />}
					endContent={<MdOutlineChevronRight className="ml-1 text-base rotate-90 text-increff-orange-500 " />
				}
				>
					<span className="text-base font-normal capitalize text-increff-orange-500">{currentMenu?.title}</span>
				</Button>
			</DropdownTrigger>
			<DropdownMenu className="w-48" variant="light">
				{siteConfig.navItems.map((item, index) => (
					<DropdownItem
						key={`${item}-${index}`}
						className={clsx(
							pathname === item.href && "shadow bg-increff-orange-50 text-increff-orange-500 data-[hover=true]:bg-increff-orange-50",
							"px-3 py-2 data-[hover=true]:text-increff-orange-500"
						)}
						onClick={() => router.push(item.href as string)}
					>
						<span className="text-sm">{item.label}</span>
					</DropdownItem>
				))}
			</DropdownMenu>
		</Dropdown>
	);

	const renderDropdown = (routes: Partial<ExtendedRoute>[], title: string, startContent: ReactNode) => {
		const isActiveDropdown = routes.some(item => item.path === currentMenu?.path);

		return (<Dropdown placement="bottom-start">
		  <DropdownTrigger>
			<Button
			  disableRipple
			  className={clsx(
				"p-0 bg-transparent data-[hover=true]:bg-transparent gap-1",
				isActiveDropdown && "text-primary"
			  )}
			  radius="sm"
			  variant="light"
			  startContent={<span className={clsx(isActiveDropdown ? "text-primary" : "text-gray-600")}>{startContent}</span>}
			  endContent={
				<MdOutlineChevronRight className={clsx("ml-1 text-base rotate-90", isActiveDropdown ? "text-primary" : "text-gray-600")} />
			  }
			>
			  <span className="text-base font-normal capitalize">{title}</span>
			</Button>
		  </DropdownTrigger>
		  <DropdownMenu className="w-48" variant="light">
			{routes.map((item, index) => (
			  <DropdownItem
				key={`${item}-${index}`}
				className={clsx(
					"px-3 py-2",
					item.path === currentMenu?.path ? "shadow bg-primary-50 text-primary-500 data-[hover=true]:bg-primary-50" : "text-gray-700",
					"data-[hover=true]:text-primary-500"
				)}
				onClick={() => router.push(item.path as string)}
			  >
				<span className="text-sm">{item.title}</span>
			  </DropdownItem>
			))}
		  </DropdownMenu>
		</Dropdown>
	)};

	const renderNavItems = () => {
		if (loading || !isAuthenticated) return null;

		switch (accessLevel) {
		  case 'admin':
			return (
			  <>
				{renderDropdown(DOMAIN_ROUTES, "Domain", <MdSupervisedUserCircle className="text-base" />)}
				<span className="px-2"></span>
				{renderDropdown(ADMIN_ROUTES, "Admin", <RiShieldUserFill className="text-base" />)}
			  </>
			);
		  case 'domain':
			return renderDropdown(DOMAIN_ROUTES, "Domain", <MdSupervisedUserCircle className="text-base" />);
		  case 'profile': return renderDropdown(PROFILE_ROUTE, "Profile", <FaCircleUser className="text-base" />);
		  default:
			return (
			  <NavbarItem>
				<NextLink href="/domain/profile">Profile</NextLink>
			  </NavbarItem>
			);
		}
	};

	const UserAvatar = (
		<div className="flex items-center pl-2 pr-3 py-0.5 border rounded-full gap-1 min-w-36 cursor-pointer">
			<Avatar
				size="sm"
				name={ userPrinciple?.name?.substring(0,1)?.toUpperCase() || ""}
				showFallback
				src={userPrinciple?.picture ?? ''}
				classNames={{
					base: "w-7 h-7 -ml-[0.15rem]",
				}}
			/>
			<span className="flex flex-col mr-1 ml-0.5 text-gray-700">
				<span className="text-sm">{userPrinciple?.name || "John Doe"}</span>
				<span className="block -mt-0.5 text-xs">{userPrinciple?.orgName || "john.doe@increff.com"}</span>
			</span>
			<MdOutlineChevronRight width="1rem" className="ml-auto rotate-90" />
		</div>
	);

	return (
		<>
		<NextUINavbar
			height="3.5rem"
			className={clsx((pathname === '/logout' || !currentMenu?.title) && "hidden" , "shadow")}
			maxWidth="full"
			position="sticky"
		>
			<NavbarContent className="basis-1/5 sm:basis-full" justify="start">
				<NavbarBrand as="li" className="gap-3 max-w-fit">
					<NextLink className="flex items-center justify-center pr-1" href="/">
						<IncreffLogo />
						<i className="mr-2 text-base text-transparent increff-client-icons increff-text bg-gradient-to-r from-increff-orange-200 to-increff-orange-400 bg-clip-text"></i>
						<p
							className="mb-0 text-lg text-gray-700"
						>
							ACCOUNT
						</p>
					</NextLink>
				</NavbarBrand>

				<NavbarItem className="hidden ml-3 sm:flex">{renderNavItems()}</NavbarItem>
			</NavbarContent>

			<NavbarContent
				className="hidden sm:flex basis-1/5 sm:basis-full"
				justify="end"
			>
				<NavbarItem className="hidden gap-2 sm:flex">
					{/* <ThemeSwitch /> */}
					<Dropdown classNames={{content: "bg-gradient-to-br from-white to-primary-50"}} placement="bottom-end">
						<DropdownTrigger>
							{UserAvatar}
						</DropdownTrigger>
						<DropdownMenu
							disabledKeys={["user_info"]}
							className="p-1"
							itemClasses={{
								base: [
									"rounded-md",
									"text-default-500",
									"transition-opacity",
									"data-[hover=true]:text-primary-500",
									"data-[hover=true]:bg-primary-100",
									"data-[selectable=true]:focus:bg-primary-100",
									"data-[pressed=true]:opacity-70",
									"data-[focus-visible=true]:ring-default-500",
								],
							}}
						>
							<DropdownSection showDivider>
								<DropdownItem
									isReadOnly
									key="user_avatar"
									className="cursor-default min-w-72"
								>
									<div className="flex flex-col items-center w-full">
										{userPrinciple?.picture ? (
											<div className="relative rounded-full shadow-none shadow-black/5 w-fit">
												<ImageWrapper
													className="relative z-10 shadow-black/5 shadow-none transition-transform-opacity motion-reduce:transition-none !duration-300 rounded-full"
													src={userPrinciple?.picture ?? ''}
													width={96}
													height={96}
												/>
												<ImageWrapper
													className="absolute inset-0 z-0 object-cover w-full h-full scale-105 translate-y-1 rounded-full filter blur-lg saturate-150 opacity-30"
													src={userPrinciple?.picture ?? ''}
													width={96}
													height={96}
												/>
											</div>
										) : (
											<div className="flex flex-col items-center">
												{!userPrinciple?.picture && (
													<div className="flex items-center justify-center w-24 h-24 text-4xl rounded-full shadow text-primary-500 profile-icon bg-primary-100/50 shadow-primary-200/70">
														{/* {'Admin User'?.trim().split(' ').map(name => name.slice(0, 1).toUpperCase()).slice(0, 2).join('')} */}
														{(userPrinciple?.fullName || userPrinciple?.name)?.trim().split(' ').map(name => name.slice(0, 1).toUpperCase()).slice(0, 2).join('')}
													</div>
												)}
											</div>
										)}
										<div className="flex flex-col items-center">
											<p className="mt-2 text-lg font-normal text-gray-600">{userPrinciple?.fullName || userPrinciple?.name || ""}</p>
											<p className="text-sm font-light text-gray-500">{userPrinciple?.email || userPrinciple?.orgName || ""}</p>
										</div>
									</div>
								</DropdownItem>
							</DropdownSection>

							<DropdownSection classNames={{group: 'flex justify-around', base: 'mb-0'}}>
								<DropdownItem
									className="w-1/2 py-3 text-gray-600 rounded-lg cursor-pointer"
									key="profile_page" 
									onClick={() => router.push(PROFILE_ROUTE[0].path as string)}
								>
									<span className="flex flex-col items-center justify-center gap-1">
										<FaCircleUser className="text-2xl" />
										<span className="text-base font-light">Profile</span>
									</span>
								</DropdownItem>

								<DropdownItem
									className="w-1/2 py-3 text-red-500 rounded-lg cursor-pointer data-[hover=true]:text-red-500 data-[hover=true]:bg-red-100"
									key="logout"
									onClick={() => router.push('/logout')}
								>
									<span className="flex flex-col items-center justify-center gap-1">
										<MdPowerSettingsNew className="text-2xl" />
										<span className="text-base font-light">Logout</span>
									</span>
								</DropdownItem>
							</DropdownSection>
						</DropdownMenu>
					</Dropdown>
				</NavbarItem>
				{/* <NavbarItem className="hidden lg:flex">{searchInput}</NavbarItem> */}
			</NavbarContent>

			<NavbarContent className="pl-4 sm:hidden basis-1" justify="end">
				{/* <ThemeSwitch /> */}
				<NavbarMenuToggle />
			</NavbarContent>

			<NavbarMenu>
				{/* {searchInput} */}
				{/* {UserAvatar}
				{ AdminDropdown } */}
				<div className="flex flex-col gap-2 mx-4 mt-2">
					{siteConfig.navMenuItems.map((item, index) => (
						<NavbarMenuItem key={`${item}-${index}`}>
							<Link
								color={
									index === 2
										? "primary"
										: index === siteConfig.navMenuItems.length - 1
										? "danger"
										: "foreground"
								}
								href="#"
								size="lg"
							>
								{item.label}
							</Link>
						</NavbarMenuItem>
					))}
				</div>
			</NavbarMenu>
		</NextUINavbar>
		<div
			className={clsx(!currentMenu?.title ? "h-1.5 bg-gradient-to-r from-red-500 to-blue-500" : "hidden")}
		></div>
		{currentMenu?.showTitleBar && (
			<div
				id="titlebar"
				className="flex items-center h-[3.125rem] px-6 py-1.5 bg-background/70 shadow gap-x-2 text-lg font-normal"
				style={{height: 'var(--titlebar-height)'}}
			>
				{currentMenu?.icon}
				<span className="text-base mb-0.5">{currentMenu?.title}</span>
				
			</div>
		)}
		</>
	);
};
